// パリィの音の作り直し (2026-10-14・開発版 ?parrySfx=a|b|c だけ・配布版は今の柳の型のまま)。
//   ユーザー原文 (10-14・音のパック P 節)
//     「元の音がそもそも前の脳汁加工されてない気がする」
//     「重さはボスの武器にしている材質や攻撃力に応じて変える感じで。大中小みたいな分け方ってよりはもっとものによるかんじに」
//     「キンキンした音が長く響いているのが強くてそれを抑えてほしい。…きれいな演奏すぎるって感じかな」
//     「ゼンゼロとかは連続で弾いたから音階が違うんじゃなくて、モーションに応じて音が変わっているように聞こえる」
//   設計・測った値 = docs/research/2026-10-14-parry-sfx-remake.md・素材と表 = scripts/render_parry_remake.py
//
//   a 乾いた一打 = 鳴りをほぼ残さない・脳汁のカチッがいちばん強い
//   b 短い鳴り   = 鋼らしさを少し残す (今の音の約半分の長さ)・鳴りは音程の合わないざらついた物
//   c 低い芯     = a の短さで、中域の「バシッ」と低い「ドン」を太く
//   1回の弾き = 材質ごとの「芯」+ 技の重さで大きく・低くなる「重さ」の2層。連続で弾いた段では変えない。
//   崩した瞬間 (体幹が尽きた) は重さを 0.85 以上に引き上げる。ボスの致命を弾いた時 (exec) は今のまま (押し返しの二段)。
//   効果音の名前は 'parry' のまま = プレイヤーの音の優先 (src/sound_priority.ts) はそのまま効く。
import { PARRY_REMAKE_PLAN } from './parry_remake_plan'
import { PARRY_SOUND_CANDIDATES, PARRY_SOUND_DEFAULT } from './parry_sound'
import { PARRY_MATERIALS, parryMaterialOf, type ParryMaterial } from './parry_material'

export type ParryRemakeSampleVariant = 'a' | 'b' | 'c'
/** 前の音 (git の履歴にあった配布版の音・audio.ts の数式で鳴らす・2026-10-14 比較用・docs/research/2026-10-14-parry-sfx-old-vs-new.md)。
 *  old-0526 = 13層の合成 (05-26〜06-10)・old-just / old-normal = 冷たい銀 (06-18〜10-09 のジャストと普段)。 */
export type ParryOldVersion = 'old-0526' | 'old-just' | 'old-normal'
export const PARRY_OLD_VERSIONS: readonly ParryOldVersion[] = ['old-0526', 'old-just', 'old-normal']
/** 直した版 (10-14・前の冷たい銀の層を土台に戻す・audio.ts の数式で鳴らす)。
 *  d = 冷たい銀 (ジャスト) をそのまま・重さだけ技の材質と攻撃力から取る (崩しは 0.85 以上)
 *  e = d に c の「重さ」の層 (技が重いほど低く大きいドン) を重ねる
 *  f = e を土台に当たった物の材質で鳴らし分ける (10-15・src/parry_material_voice.ts)
 *  ga1〜ga3 = e の低いドンを残し、ゼンゼロの「ガキン」の2層 (ガ＝広い帯の短い打撃・キン＝合わない金属の鳴り) を重ねる (10-16)。
 *    数字は高い所の強さの段 (ga1 弱め → ga3 低いドンと同じくらい)。大きさは e とそろえる。
 *    手本の測定 = docs/research/2026-10-15-zzz-parry-gakin-layers.md・docs/research/2026-10-16-zzz-parry-quiet-cases-answered.md */
export type ParryGakinVersion = 'ga1' | 'ga2' | 'ga3'
/** gb1〜gb3 = 土台の「ぽよん」を外してガキンを重ねる (10-16 ユーザー「もともと使っている音がぽよんって感じの音だからそこを変えないといけない」)。
 *    ゼンゼロからは特徴だけ取り (一瞬の広いガ・和音にならない鳴り・音程の動かない低いド)、この作品の色は残す
 *    (冷たい銀の鋼・鎖の鈴・6kHz より上を削る灰の王城の約束。10-16 ユーザー「ゼンゼロの音そのまま使っちゃダメ」)。
 *    gb1 = 音程が滑る層 (下がるボヨン・上がるよん) だけ外す / gb2 = さらに低い純音 (59Hz・72Hz・220Hz) を雑音のドンへ /
 *    gb3 = gb2 のガとキンを強める。
 *  gb4 = gb3 に、弾いた瞬間の「下から押す」低い雑音を足す (10-17 耳で決める4問の答え：響きは今の gb3・続けても毎回同じ音・
 *    押しは強い版・尾は短め。docs/research/2026-10-17-zzz-parry-ear4-candidates.md)。尾は gb3 のまま伸ばさない。押しの3問 (10-17) で gb3 に負けたので開発版だけ。
 *    gb4s = 押しを弱めた別テイク / gb4h = 押しをさらに強めた別テイク (開発版で聞き比べる)。 */
/** gh1・gh2・gk1・ghk = gb3 を「高く・気持ちよく」した候補 (10-18 ユーザー「パリィの音もう少し高い音というか気持ちいい音にしてほしい」)。
 *    「高い」が芯の高さか明るさかを決めつけず両方を振る (docs/research/2026-10-18-parry-higher-candidates.md)。
 *    gh1 = 鋼の芯とキンを少し高く (+3 半音) / gh2 = はっきり高く (+6 半音・低いドンを少し控える) /
 *    gk1 = 芯の高さは gb3 のまま、高い所を開けてきらめきを足す (鎖の鈴を強め・6kHz の削りをゆるめる) / ghk = gh1 と gk1 の両方。
 *    大きさは gb3 とそろえた (trimDb)。 */
export type ParryHighVersion = 'gh1' | 'gh2' | 'gk1' | 'ghk'
/** 冷たい銀 (ジャスト) の加工した層を高くした候補へ戻す版 (10-18)。 */
export type ParrySoulVersion = 'gs1' | 'gs2' | 'gs3' | 'gs4'
export type ParryBaseVersion = 'gb1' | 'gb2' | 'gb3' | 'gb4' | 'gb4s' | 'gb4h' | ParryHighVersion | ParrySoulVersion
export type ParryFixVersion = 'd' | 'e' | 'f' | ParryGakinVersion | ParryBaseVersion
export const PARRY_GAKIN_VERSIONS: readonly ParryGakinVersion[] = ['ga1', 'ga2', 'ga3']
export const PARRY_HIGH_VERSIONS: readonly ParryHighVersion[] = ['gh1', 'gh2', 'gk1', 'ghk']
export const PARRY_SOUL_VERSIONS: readonly ParrySoulVersion[] = ['gs1', 'gs2', 'gs3', 'gs4']
export const PARRY_BASE_VERSIONS: readonly ParryBaseVersion[] = ['gb1', 'gb2', 'gb3', 'gb4', 'gb4s', 'gb4h', ...PARRY_HIGH_VERSIONS, ...PARRY_SOUL_VERSIONS]
export const PARRY_FIX_VERSIONS: readonly ParryFixVersion[] = ['d', 'e', 'f', ...PARRY_GAKIN_VERSIONS, ...PARRY_BASE_VERSIONS]
/** 土台から外す物と、ガキンの層の強さ (dB)・全体の大きさを e とそろえる倍率 (dB・ゲームの出力を録って決めた)。
 *  pushDb = 弾いた瞬間の低い押しの強さ (null = 鳴らさない)。
 *  tone (gh・gk だけ) = pitch: 鋼の芯・スナップ・380Hz の芯・ガとキンの高さに掛ける / shelfDb: 鋼の 5200Hz より上の削り (gb3 は -6) /
 *    ashHz: ガとキンを切る高さ (gb3 は 6000) / bellDb: 鎖の鈴とジャストの鐘の強さ / thudDb: 雑音のドンの強さ。 */
export type ParryTone = { pitch: number; shelfDb: number; ashHz: number; bellDb: number; thudDb: number }
export const PARRY_TONE_GB3: ParryTone = { pitch: 1, shelfDb: -6, ashHz: 6000, bellDb: 0, thudDb: 0 }
export const PARRY_BASE_PLAN: Record<ParryBaseVersion, { cutSweep: boolean; noiseLow: boolean; layerDb: number; trimDb: number; pushDb: number | null; tone?: ParryTone; soulDb?: number }> = {
  gb1: { cutSweep: true, noiseLow: false, layerDb: -6, trimDb: 0, pushDb: null },
  gb2: { cutSweep: true, noiseLow: true, layerDb: -6, trimDb: 0, pushDb: null },
  gb3: { cutSweep: true, noiseLow: true, layerDb: 0, trimDb: 0, pushDb: null },
  gb4: { cutSweep: true, noiseLow: true, layerDb: 0, trimDb: 0, pushDb: 0 },
  gb4s: { cutSweep: true, noiseLow: true, layerDb: 0, trimDb: 0, pushDb: -6 },
  gb4h: { cutSweep: true, noiseLow: true, layerDb: 0, trimDb: 0, pushDb: 4 },
  gh1: { cutSweep: true, noiseLow: true, layerDb: 0, trimDb: 1.0, pushDb: null, tone: { pitch: 2 ** (3 / 12), shelfDb: -6, ashHz: 6000, bellDb: 0, thudDb: 0 } },
  gh2: { cutSweep: true, noiseLow: true, layerDb: 0, trimDb: 1.1, pushDb: null, tone: { pitch: 2 ** (6 / 12), shelfDb: -6, ashHz: 6000, bellDb: 0, thudDb: -3 } },
  gk1: { cutSweep: true, noiseLow: true, layerDb: 0, trimDb: -1.6, pushDb: null, tone: { pitch: 1, shelfDb: 0, ashHz: 9000, bellDb: 4, thudDb: 0 } },
  ghk: { cutSweep: true, noiseLow: true, layerDb: 0, trimDb: -0.8, pushDb: null, tone: { pitch: 2 ** (3 / 12), shelfDb: 0, ashHz: 9000, bellDb: 4, thudDb: 0 } },
  // gs (10-18): もりしさん「前のパリの音みたいに合成音みたいな加工した音を混ぜてほしい」→ 前の音＝冷たい銀のジャスト (old-just)。
  //   soulDb = 冷たい銀の「上がる尾」(1600→2400Hz へ昇る正弦・残響多め) を戻す強さ (old-just を 0)。ガキンに埋もれるので少し上げる。
  //   gs1 = gh1 に尾 / gs2 = gs1 のガキンを引いて冷たい銀を前へ / gs3 = ghk に尾 / gs4 = gs2 に昔の低い層 (下がる slam・220Hz・72Hz) も戻す
  gs1: { cutSweep: true, noiseLow: true, layerDb: 0, trimDb: 0.5, pushDb: null, tone: { pitch: 2 ** (3 / 12), shelfDb: -6, ashHz: 6000, bellDb: 0, thudDb: 0 }, soulDb: 4 },
  gs2: { cutSweep: true, noiseLow: true, layerDb: -6, trimDb: 1.0, pushDb: null, tone: { pitch: 2 ** (3 / 12), shelfDb: -6, ashHz: 6000, bellDb: 0, thudDb: 0 }, soulDb: 4 },
  gs3: { cutSweep: true, noiseLow: true, layerDb: 0, trimDb: -1.4, pushDb: null, tone: { pitch: 2 ** (3 / 12), shelfDb: 0, ashHz: 9000, bellDb: 4, thudDb: 0 }, soulDb: 4 },
  gs4: { cutSweep: false, noiseLow: false, layerDb: -6, trimDb: 2.2, pushDb: null, tone: { pitch: 2 ** (3 / 12), shelfDb: -6, ashHz: 6000, bellDb: 0, thudDb: 0 }, soulDb: 4 },
}
/** 配布版で鳴らすパリィの音 (10-17〜 gb3。gb4 を一度入れたが、押しの3問で gb3 ＞ 押し控えめ ＞ gb4 ＞ 押し強め と答えが出て gb3 に差し替えた)。開発版で ?parrySfx= を付けない時もこれ。'off' を付けると 10-09〜10-17 の配布版の音 (柳の型)。 */
export const PARRY_SFX_RELEASE: ParryRemakeVariant = 'gb3'
export function isParryBaseVersion(v: ParryRemakeVariant): v is ParryBaseVersion {
  return (PARRY_BASE_VERSIONS as readonly string[]).includes(v)
}
/** c の重さの層 (c_weight.wav＝低い純音) を重ねる版。gb2・gb3 は純音を外すので読まない。 */
function usesWeightLayer(v: ParryRemakeVariant): boolean {
  return v === 'e' || v === 'f' || isParryGakinVersion(v) || v === 'gb1'
}
/** ガキンの層の強さ (dB・ga3 を 0 とする) と、全体の大きさを e とそろえる倍率 (dB・ゲームの出力を録って決めた)。 */
export const PARRY_GAKIN_LEVEL: Record<ParryGakinVersion, { layerDb: number; trimDb: number }> = {
  ga1: { layerDb: -12, trimDb: 0 },
  ga2: { layerDb: -6, trimDb: 0 },
  ga3: { layerDb: 0, trimDb: 0 },
}
export function isParryGakinVersion(v: ParryRemakeVariant): v is ParryGakinVersion {
  return (PARRY_GAKIN_VERSIONS as readonly string[]).includes(v)
}
export type ParryRemakeVariant = 'off' | ParryRemakeSampleVariant | ParryOldVersion | ParryFixVersion
export const PARRY_REMAKE_LABELS: Record<ParryRemakeSampleVariant, string> = {
  a: '乾いた一打', b: '短い鳴り', c: '低い芯',
}

export function parseParryRemakeVariant(raw: string | null | undefined): ParryRemakeVariant {
  if (raw === 'off') return 'off'
  if (raw === 'a' || raw === 'b' || raw === 'c') return raw
  if ((PARRY_FIX_VERSIONS as readonly string[]).includes(raw ?? '')) return raw as ParryFixVersion
  return (PARRY_OLD_VERSIONS as readonly string[]).includes(raw ?? '') ? raw as ParryOldVersion : PARRY_SFX_RELEASE
}
export function isParryFixVersion(v: ParryRemakeVariant): v is ParryFixVersion {
  return (PARRY_FIX_VERSIONS as readonly string[]).includes(v)
}
/** 直した版の重さ (0..1)。技の材質と攻撃力から・崩した瞬間は 0.85 以上。 */
export function parryFixWeight(hit: ParryRemakeHit): number {
  let w = hit.attackId ? parryMaterialOf(hit.attackId, hit.weight).weight : Math.max(0, Math.min(1, hit.weight ?? 0.35))
  if (hit.broke) w = Math.max(w, BROKE_MIN_WEIGHT)
  return w
}
export function isParryOldVersion(v: ParryRemakeVariant): v is ParryOldVersion {
  return (PARRY_OLD_VERSIONS as readonly string[]).includes(v)
}
let variant: ParryRemakeVariant = PARRY_SFX_RELEASE
export function setParryRemakeVariant(raw: string | null | undefined): ParryRemakeVariant {
  variant = parseParryRemakeVariant(raw); return variant
}
export function parryRemakeVariant(): ParryRemakeVariant { return variant }

// 得物の材質で版を使い分ける (10-18・配布版)。金の爪と牙は gs3 (きらめき)・ほかは gs1 (ガキンそのまま・重い得物は雑音のドンも鳴る)。
//   ユーザー原文 (10-18・答えるページ問5のメモ)「３以外の音を相手に技の重さや武器の質によって変える感じで調整するイメージが良いかも？」
//   検収パック kensyu-sound-1018 の答え (10-18)：Q1＝得物で変える／Q2 重い得物＝今のドン＋ガキン (gs1)／Q3 刀・槍・薄衣＝ガキンそのまま (gs1)。
//   案 = docs/research/2026-10-18-parry-sound-by-weapon-proposal.md。重さは今の parryFixWeight (連続) をどの版でもそのまま使う。
//   開発版：?parryByWeapon=0 で切る (?parrySfx= を付けた時も切れる)・?parryHeavy=gs4・?parryThin=gs2 で負けた案を聞き直せる。
export type ParryHeavyPick = 'gs4' | 'gs1'
export type ParryThinPick = 'gs2' | 'gs1'
export type ParryByWeapon = { on: boolean; heavy: ParryHeavyPick; thin: ParryThinPick }
export const PARRY_BY_WEAPON_RELEASE: ParryByWeapon = { on: true, heavy: 'gs1', thin: 'gs1' }
/** sfxGiven = ?parrySfx= が付いている (その版を聞き比べたい時は、得物での使い分けを既定で切る)。 */
export function parseParryByWeapon(on: string | null | undefined, heavy?: string | null, thin?: string | null, sfxGiven = false): ParryByWeapon {
  return { on: on === '1' || (on !== '0' && !sfxGiven), heavy: heavy === 'gs4' ? 'gs4' : 'gs1', thin: thin === 'gs2' ? 'gs2' : 'gs1' }
}
/** 材質 → 版。金の爪は gs3 (きらめき)・柄と砲の弾は gs1 (ガキンで硬く止める)。 */
export function parryVersionForMaterial(m: ParryMaterial, pick: ParryByWeapon): ParrySoulVersion {
  if (m === 'claw') return 'gs3'
  if (m === 'haft' || m === 'shell') return 'gs1'
  if (m === 'greatblade' || m === 'iron') return pick.heavy
  return pick.thin
}
let byWeapon: ParryByWeapon = PARRY_BY_WEAPON_RELEASE
export function setParryByWeapon(p: ParryByWeapon): ParryByWeapon { byWeapon = p; return byWeapon }
export function parryByWeapon(): ParryByWeapon { return byWeapon }
/** いま弾いた技で鳴らす版。使い分けが切ならそのまま v。材質を直に渡されたらそれを使い、技が分からない時は刀。 */
export function parryVoiceVersion(v: ParryRemakeVariant, attackId: string | undefined, material?: ParryMaterial, pick: ParryByWeapon = byWeapon): ParryRemakeVariant {
  return pick.on ? parryVersionForMaterial(material ?? parryMaterialOf(attackId).material, pick) : v
}

/** 開発版で案を選んだ時だけ読む素材 [キー, URL]。 */
export function parryRemakeFiles(v: ParryRemakeVariant): [string, string][] {
  if (v === 'off' || isParryOldVersion(v) || v === 'd') return []
  if (usesWeightLayer(v)) return [['parry_remake_c_weight', '/sfx/parry_remake/c_weight.wav?v=1']]
  if (isParryFixVersion(v)) return []
  return [...PARRY_MATERIALS.map((m) => `${v}_${m}`), `${v}_weight`]
    .map((k) => [`parry_remake_${k}`, `/sfx/parry_remake/${k}.wav?v=1`])
}

export type ParryRemakeHit = { attackId?: string; weight?: number; broke?: boolean; material?: ParryMaterial }
export type ParryRemakeLayer = { sample: string; rate: number; gain: number }

/** e・f・ga・gb1 の重さの層 (c の重さの層だけ)。d・gb2・gb3・それ以外は null。f の大きさは audio.ts が材質で掛ける。 */
export function planParryFixWeight(hit: ParryRemakeHit, v: ParryRemakeVariant = variant): ParryRemakeLayer | null {
  if (!usesWeightLayer(v)) return null
  return planParryRemake(hit, 'c')?.[1] ?? null
}
const BASE_GAIN = 10 ** (PARRY_SOUND_CANDIDATES[PARRY_SOUND_DEFAULT].gainDb / 20)
export const PARRY_REMAKE_REVERB = PARRY_SOUND_CANDIDATES[PARRY_SOUND_DEFAULT].reverbSend * 0.6
export const BROKE_MIN_WEIGHT = 0.85

/** 弾いた瞬間に鳴らす2層。案を選んでいない時は null (= 今の音)。 */
export function planParryRemake(hit: ParryRemakeHit, v: ParryRemakeVariant = variant): ParryRemakeLayer[] | null {
  if (v === 'off' || isParryOldVersion(v) || isParryFixVersion(v)) return null
  const fromAtk = parryMaterialOf(hit.attackId, hit.weight)
  const material = hit.material ?? fromAtk.material
  let w = hit.material && !hit.attackId ? Math.max(0, Math.min(1, hit.weight ?? 0.35)) : fromAtk.weight
  if (hit.broke) w = Math.max(w, BROKE_MIN_WEIGHT)
  const rows = PARRY_REMAKE_PLAN.variants[v][material]
  // w の前後2点を直線でつなぐ (倍率は dB で)
  let i = 0
  while (i < rows.length - 2 && w > rows[i + 1].w) i += 1
  const a = rows[i], b = rows[i + 1]
  const t = Math.max(0, Math.min(1, (w - a.w) / (b.w - a.w)))
  const lerp = (x: number, y: number) => x + (y - x) * t
  const lerpDb = (x: number, y: number) => 10 ** lerp(Math.log10(x), Math.log10(y))
  return [
    { sample: `parry_remake_${v}_${material}`, rate: lerp(a.coreRate, b.coreRate), gain: lerpDb(a.coreGain, b.coreGain) * BASE_GAIN },
    { sample: `parry_remake_${v}_weight`, rate: lerp(a.weightRate, b.weightRate), gain: lerpDb(a.weightGain, b.weightGain) * BASE_GAIN },
  ]
}

// 余韻 (配布版＝humWc・PARRY_TAIL_RELEASE。ほかの案は開発版 ?parryTail= の比較用)。
//   10-18 の正弦6本の鳴り (ring) は もりしさん (kensyu-sound-1018b・原文)「この余韻の作り方は鐘の音を足したような感じでダメ」・
//     続けて弾くと「重なってうるさい」。
//   ゼンゼロの余韻の中身 (docs/research/2026-10-19-zzz-parry-tail-content.md)：線は 2.8〜6.7kHz にだけあり高さは動かない・低い線ほど長く残る・
//     線の間の雑音の床も一緒に上がる・低い帯は 38〜60Hz の唸り。1〜2kHz の低い線 (ring の 1180・2310Hz) は無い。
//
// 金属の板の響き (10-19 作り直し・前回の案。比べるために残す)。
//   弾いた音そのものを 2.4〜6.8kHz に切って「金属の板の響き」(雑音の残響＋線8本) へ通す。
//   plate = ゼンゼロの中高の帯の量へ合わせた物 / plateShort = 減り方を倍速くした控えめな物 / plateLow = plate に低い唸りを足す。
//   sendGain は響きの量・irSec は響きの元の長さ・scale は減り方の時間の倍率・rumble は唸りの量 (手元で録音に掛けて測って決めた)。
//   続けて弾いた時は前の余韻を 40ms で切る (src/audio.ts playParryTail)。
//   答え (10-19・kensyu-sound-1019・原文)「今色々作ってくれたのは反響してるだけって感じ　ゼンゼロの音はどうしてる？確かかはわからないけど、
//     唸りっていってる表現の仕方が近い気がする」・続けて弾くと「まだうるさい」。
export type ParryPlateTail = 'plate' | 'plateShort' | 'plateLow'
export const PARRY_PLATE_TAILS: readonly ParryPlateTail[] = ['plate', 'plateShort', 'plateLow']
export type ParryTailPlan = { sendGain: number; irSec: number; scale: number; rumble: number | null }
export const PARRY_TAIL_PLAN: Record<ParryPlateTail, ParryTailPlan> = {
  plate: { sendGain: 3.0, irSec: 3.0, scale: 1, rumble: null },
  plateShort: { sendGain: 2.45, irSec: 1.6, scale: 0.5, rumble: null },
  plateLow: { sendGain: 3.0, irSec: 3.0, scale: 1, rumble: 7 },
}

// 唸りが主役の余韻 (10-20・docs/research/2026-10-20-parry-tail-hum.md)。反響 (板の響き) を主役にせず、38〜60Hz の低い唸りだけを残す。
//   hum = 唸りだけ / humLine = 唸り＋高い所の細い線2本 (雑音の残響なし) / humSway = 唸りがゆっくり揺れる。
//   humGain は唸りの量・humSec は重さ 1 の時に消えるまでの秒数 (T60)・lineGain は細い線の量 (humLine だけ)・
//   swayHz と swayDepth は揺れの速さと深さ (humSway だけ)。大きさは3案で同じになるよう録音に掛けて測って決めた。
//   重さ (0..1) が重いほど大きく長い。続けて弾いた時は唸りを足し直さず、量を下げる (PARRY_HUM_CHAIN)。
//
// 唸りで重さを出す・揺れは技で使い分ける (10-21・docs/research/2026-10-21-parry-hum-weight-sway.md)。
//   答え (10-19・kensyu-sound-1020・原文)「細い線の余韻は耳にキンと残る感じがする。気持ち良いと言うよりは少し耳障り」・
//     「唸りだけと揺れる唸りで技によって違いを出すべきかも 重さで揺れ具合を変えるとか。揺れをつけるつけないは何で決めるべきなのかはわからない」・
//     「今のパリィの音が割と軽く聞こえるので、うなりと揺れるうなりで重さを出すといいと思いました」。
//   humCore = 弾いた瞬間から低い芯 (45〜90Hz の雑音) が鳴り、そのまま唸りへつながる。芯の量は重さの段ごとに上がる。揺れなし。
//   humW = humCore に揺れを足す。揺れるかどうかは技の重さで決める (重さ 0.4 以上で揺れ、重いほど深くゆっくり)。
//   humM = humCore に揺れを足す。揺れるかどうかは得物で決める (幅広の刃・鉄の塊・槍・砲の弾 = 大きな金属が震える物だけ)。深さは humW と同じく重さで。
//   rule = 揺れの決め方・core = 芯の量 (null = 芯なし)・attackSec = 唸りが山へ上がる秒数・floor = 一番軽い技の唸りの量 (重さ 1 を 1 とした割合)。
//
// humW を遊びの中で抑える (10-22・docs/research/2026-10-22-parry-hum-quieter.md)。
//   答え (10-21・kensyu-sound-1021-hum・原文)「重くなった」「重さで決める」・戴冠獣の噛みは「揺れない方がいい」・
//     遊んで弾くと「重すぎる・うるさい」・配布版は「まだ入れない（直してほしい）」。
//   3案とも揺れは重い段 (重さ 0.6 以上) だけ・続けて弾いた2打目からは芯も唸りも humW より下げる。違いは量と長さ。
//   humWa = 少し抑える (唸りと芯 −3dB・唸り 3 秒) / humWb = しっかり抑える (唸りと芯 −6dB・唸り 2.2 秒・芯も短く) /
//   humWc = 重い技だけ (軽い技は唸りも芯もほぼ無し・重い段から humW に近い量)。
//   swayFrom = 揺れ始める重さ・humFrom = 唸りが 0 から上がり始める重さ (無ければ floor の式)・coreSteps = 芯の段・
//   coreSec = 芯の長さの倍率・humChain / coreChain = 続けて弾いた時の量。
export type ParryHumTail = 'hum' | 'humLine' | 'humSway' | 'humCore' | 'humW' | 'humM' | 'humWa' | 'humWb' | 'humWc'
export const PARRY_HUM_TAILS: readonly ParryHumTail[] = ['hum', 'humLine', 'humSway', 'humCore', 'humW', 'humM', 'humWa', 'humWb', 'humWc']
export type ParryHumSwayRule = 'none' | 'always' | 'weight' | 'material'
export type ParryHumPlan = {
  humGain: number; humSec: number; lineGain: number | null; swayHz: number | null; swayDepth: number
  rule: ParryHumSwayRule; core: number | null; attackSec: number; floor: number
  swayFrom?: number; humFrom?: number; coreSteps?: readonly (readonly [number, number])[]; coreSec?: number
  humChain?: readonly number[]; coreChain?: readonly number[]
  /** 崩した一打では余韻を鳴らさない (鳴っている余韻も切る)。 */
  breakSilent?: boolean
}
const HUM_10_20 = { rule: 'none' as ParryHumSwayRule, core: null, attackSec: 0.05, floor: 0.45 }
const HUM_10_21 = { humSec: 4.0, lineGain: null, swayHz: null, swayDepth: 0, attackSec: 0.015, floor: 0.3 }
const HUM_10_22 = { swayFrom: 0.6, humChain: [1, 0.35, 0.2], coreChain: [1, 0.5, 0.35] }
export const PARRY_HUM_PLAN: Record<ParryHumTail, ParryHumPlan> = {
  hum: { humGain: 30, humSec: 4.0, lineGain: null, swayHz: null, swayDepth: 0, ...HUM_10_20 },
  humLine: { humGain: 30, humSec: 4.0, lineGain: 1, swayHz: null, swayDepth: 0, ...HUM_10_20 },
  humSway: { humGain: 40, humSec: 4.0, lineGain: null, swayHz: 2.6, swayDepth: 0.55, ...HUM_10_20, rule: 'always' },
  humCore: { humGain: 34, core: 1, rule: 'none', ...HUM_10_21 },
  humW: { humGain: 34, core: 1, rule: 'weight', ...HUM_10_21 },
  humM: { humGain: 34, core: 1, rule: 'material', ...HUM_10_21 },
  humWa: { humGain: 24, core: 0.7, rule: 'weight', ...HUM_10_21, humSec: 3.0, ...HUM_10_22 },
  humWb: { humGain: 17, core: 0.5, rule: 'weight', ...HUM_10_21, humSec: 2.2, coreSec: 0.7, ...HUM_10_22 },
  humWc: { humGain: 34, core: 1, rule: 'weight', ...HUM_10_21, humSec: 3.0, humFrom: 0.45, coreSteps: [[0, 0], [0.4, 0.35], [0.6, 1.3], [0.85, 2.0]], ...HUM_10_22, breakSilent: true },
}
/** 揺れる得物 (humM)。大きな金属の塊・長い鋼が震える物。爪・刀・薄衣・柄は揺れない。 */
export const PARRY_HUM_SWAY_MATERIALS: readonly ParryMaterial[] = ['greatblade', 'iron', 'lance', 'shell']
/** 揺れの物差し：重さ from で揺れ始め (浅く速い)、full で一番深くゆっくり。 */
export const PARRY_HUM_SWAY = { from: 0.4, full: 0.85, depth: [0.3, 0.7] as const, hz: [3.2, 1.8] as const }
export type ParryHumSway = { hz: number; depth: number }
/** この一打の揺れ (揺れない時は null)。w = 技の重さ・material = 得物。 */
export function parryHumSwayOf(plan: ParryHumPlan, w: number, material: ParryMaterial): ParryHumSway | null {
  if (plan.rule === 'none') return null
  if (plan.rule === 'always') return plan.swayHz === null ? null : { hz: plan.swayHz, depth: plan.swayDepth }
  const from = plan.swayFrom ?? PARRY_HUM_SWAY.from
  if (plan.rule === 'weight' && w < from) return null
  if (plan.rule === 'material' && !PARRY_HUM_SWAY_MATERIALS.includes(material)) return null
  const k = Math.max(0, Math.min(1, (w - from) / (PARRY_HUM_SWAY.full - from)))
  const [d0, d1] = PARRY_HUM_SWAY.depth, [h0, h1] = PARRY_HUM_SWAY.hz
  return { hz: h0 + (h1 - h0) * k, depth: d0 + (d1 - d0) * k }
}
/** 揺らすと平均の大きさが下がる分を戻す倍率 (揺れの中心 1-d/2・幅 ±d/2 の実効値で割る)。 */
export function parryHumSwayMakeup(depth: number): number {
  const c = 1 - depth / 2, a = depth / 2
  return 1 / Math.sqrt(c * c + a * a / 2)
}
/** 芯の重さの段 (軽い・中・重い・崩した)。段ごとに芯の量が上がる。 */
export const PARRY_HUM_CORE_STEPS: readonly (readonly [number, number])[] = [[0, 0.45], [0.4, 1], [0.6, 1.7], [BROKE_MIN_WEIGHT, 2.4]]
/** 続けて弾いた時の芯 (芯は一打ごとに鳴る。唸りほどは下げない)。 */
export const PARRY_HUM_CORE_CHAIN: readonly number[] = [1, 0.7, 0.55]
/** 芯の量 (芯の無い案は 0)。 */
export function parryHumCoreLevel(plan: ParryHumPlan, w: number, n = 0): number {
  if (plan.core === null) return 0
  const steps = plan.coreSteps ?? PARRY_HUM_CORE_STEPS
  const chain = plan.coreChain ?? PARRY_HUM_CORE_CHAIN
  let step = steps[0][1]
  for (const [from, v] of steps) if (w >= from) step = v
  return plan.core * step * chain[Math.min(n, chain.length - 1)]
}
/** 芯が消えるまでの秒数 (T60)。重いほど長い。plan.coreSec があれば掛ける。 */
export function parryHumCoreT60(w: number, plan?: ParryHumPlan): number {
  return (0.35 + 0.65 * Math.max(0, Math.min(1, w))) * (plan?.coreSec ?? 1)
}
// 続けて弾いた時 (前の一打から windowSec 以内)。唸りは重ねも鳴らし直しもせず、鳴っている1本を n 打目の量 (1 打目の hum[n] 倍) へ rampSec で寄せる。
//   細い線は一打ごとに足すが、n 打目は line 倍に減らす。前の線は切らずに自然に消える。
export const PARRY_HUM_CHAIN = { windowSec: 1.2, rampSec: 0.1, hum: [1, 0.5, 0.35] as readonly number[], line: [1, 0.3, 0.15] as readonly number[] }

export type ParryTail = ParryPlateTail | ParryHumTail
export const PARRY_TAILS: readonly ParryTail[] = [...PARRY_PLATE_TAILS, ...PARRY_HUM_TAILS]
export function isParryHumTail(t: ParryTail | null): t is ParryHumTail {
  return (PARRY_HUM_TAILS as readonly string[]).includes(t ?? '')
}
/** 何打目か (0 = 1 打目)。前の一打が windowSec 以内なら続き。 */
export function parryHumChainIndex(prevAt: number | null, prevN: number, t: number): number {
  if (prevAt === null || t - prevAt > PARRY_HUM_CHAIN.windowSec || t < prevAt) return 0
  return Math.min(prevN + 1, PARRY_HUM_CHAIN.hum.length - 1)
}
/** 唸りの量。重さ w (0..1) と何打目か n。n 打目は 1 打目の上限 PARRY_HUM_CHAIN.hum[n] 倍。 */
export function parryHumLevel(plan: ParryHumPlan, w: number, n = 0): number {
  const chain = plan.humChain ?? PARRY_HUM_CHAIN.hum
  const k = chain[Math.min(n, chain.length - 1)]
  const c = Math.max(0, Math.min(1, w))
  if (plan.humFrom !== undefined) return plan.humGain * Math.max(0, (c - plan.humFrom) / (1 - plan.humFrom)) * k
  return plan.humGain * (plan.floor + (1 - plan.floor) * c) * k
}
/** 細い線の量 (humLine 以外は 0)。 */
export function parryHumLineLevel(plan: ParryHumPlan, w: number, n = 0): number {
  if (plan.lineGain === null) return 0
  const k = PARRY_HUM_CHAIN.line[Math.min(n, PARRY_HUM_CHAIN.line.length - 1)]
  return plan.lineGain * (0.6 + 0.4 * Math.max(0, Math.min(1, w))) * k
}
/** 唸りが消えるまでの秒数 (T60)。重いほど長い。 */
export function parryHumT60(plan: ParryHumPlan, w: number): number {
  return plan.humSec * (0.65 + 0.35 * Math.max(0, Math.min(1, w)))
}

// 配布版の余韻 (10-20・答えるページ answers/parry-hum-1020)：humWc＝重い技だけ唸る・崩した一打は余韻なし。
//   答え＝軽い・中の技／続けて弾く／重い技／配布版に入れるなら＝humWc、崩し＝余韻なし。docs/research/2026-10-22-parry-hum-quieter.md。
//   開発版は ?parryTail= で比較用の案へ・?parryTail=off で余韻なし (10-20 までの配布版)。
export const PARRY_TAIL_RELEASE: ParryTail = 'humWc'
export function parseParryTail(raw: string | null | undefined): ParryTail | null {
  return (PARRY_TAILS as readonly string[]).includes(raw ?? '') ? raw as ParryTail : null
}
let tail: ParryTail | null = PARRY_TAIL_RELEASE
/** 余韻の案を選ぶ。null・'off'・知らない名前は余韻なし。URL 指定なしの既定は PARRY_TAIL_RELEASE (main.ts が渡す)。 */
export function setParryTail(raw: string | null | undefined): ParryTail | null { tail = parseParryTail(raw); return tail }
/** この一打で鳴らす唸り (鳴らさない時は null)。崩した一打は breakSilent の案なら鳴らさない。 */
export function parryHumFor(t: ParryTail | null, broke: boolean | undefined): ParryHumTail | null {
  if (!isParryHumTail(t)) return null
  return broke && PARRY_HUM_PLAN[t].breakSilent ? null : t
}
export function parryTail(): ParryTail | null { return tail }
